import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Card, TextButton, Typography, useOverlayContext } from 'seagull';
import { AnchorLogo } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import styles from './AcceptInvitationPage.module.scss';
const deprecatedTestIds = {
    AcceptInvitationPageTestIds: {
        Logo: 'logo',
        InvitationTitle: 'invitation-title',
        InvitationDescription: 'invitation-description',
        InvitationEmail: 'invitation-email',
        InvitationCard: 'invitation-card',
        InvitationBusinessName: (businessName)=>"invitation-business-name-".concat(businessName),
        InvitationAcceptButton: (inviteId)=>"invitation-accept-button-".concat(inviteId),
        InvitationStartNewBusinessLink: 'invitation-start-new-business-link',
        InvitationStartNewBusinessLinkButton: 'invitation-start-new-business-link-button',
        ErrorToast: 'failed-to-accept-invitation-error-toast'
    }
};
export const GetAcceptInvitationPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds
    });
const AcceptInvitationPage = (param)=>{
    let { setShowInvitationPage } = param;
    const { AcceptInvitationPageTestIds } = useMemo(()=>GetAcceptInvitationPageTID(), []);
    const userEmail = useStore(selectors.user.email);
    const teamInvites = useStore(selectors.teamInvites);
    const { api, logBI } = useStoreActions();
    const { locationReload } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const handleAcceptInvitation = async (param)=>{
        let { invitationId, businessId } = param;
        const response = await handleRequest(api.business.acceptTeamInvite({
            invitationId,
            businessId
        }));
        if (response.success) {
            var _teamInvites_find;
            setShowInvitationPage(false);
            logBI(19101, {
                invitingBusinessName: (_teamInvites_find = teamInvites.find((invite)=>invite.inviteId() === invitationId)) === null || _teamInvites_find === void 0 ? void 0 : _teamInvites_find.businessName(),
                invitingBusinessId: businessId
            });
            locationReload();
        } else {
            const errorText = response.error.hasServerError(ServerError.InvalidTeamInvitation) ? response.error.invalidTeamInvitationErrorText() : 'Failed to accept invitation';
            showErrorToast(errorText, AcceptInvitationPageTestIds.ErrorToast());
        }
    };
    const handleStartNewBusiness = ()=>{
        setShowInvitationPage(false);
    };
    useOnMount(()=>{
        logBI(19100);
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.acceptInvitationPageContainer,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles.logoContainer,
                "data-testid": AcceptInvitationPageTestIds.Logo(),
                children: /*#__PURE__*/ _jsx(AnchorLogo, {
                    theme: "dark",
                    height: 56
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h3",
                weight: "normal",
                testId: AcceptInvitationPageTestIds.InvitationTitle(),
                children: "You’ve been invited to join your team on Anchor"
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraph1",
                skin: "secondary",
                testId: AcceptInvitationPageTestIds.InvitationDescription(),
                children: [
                    "We found pending invitations for",
                    ' ',
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "normal",
                        skin: "secondary",
                        as: "span",
                        testId: AcceptInvitationPageTestIds.InvitationEmail(),
                        children: userEmail
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.invitationsContainer,
                children: teamInvites.map((teamInvite)=>/*#__PURE__*/ _jsxs(Card, {
                        bi: {
                            name: 'team_invitation_card'
                        },
                        className: styles.invitationContainer,
                        testId: AcceptInvitationPageTestIds.InvitationCard(),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                weight: "normal",
                                ellipsis: true,
                                testId: AcceptInvitationPageTestIds.InvitationBusinessName(teamInvite.businessName()),
                                children: teamInvite.businessName()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'accept_team_invitation'
                                },
                                size: "small",
                                testId: AcceptInvitationPageTestIds.InvitationAcceptButton(teamInvite.inviteId()),
                                onClick: ()=>handleAcceptInvitation({
                                        invitationId: teamInvite.inviteId(),
                                        businessId: teamInvite.businessId()
                                    }),
                                children: "Accept invitation"
                            })
                        ]
                    }, teamInvite.businessName()))
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                variant: "paragraph1",
                skin: "secondary",
                testId: AcceptInvitationPageTestIds.InvitationStartNewBusinessLink(),
                children: [
                    "Looking to start a new business instead?",
                    ' ',
                    /*#__PURE__*/ _jsx(TextButton, {
                        onClick: handleStartNewBusiness,
                        testId: AcceptInvitationPageTestIds.InvitationStartNewBusinessLinkButton(),
                        children: "Click here"
                    })
                ]
            })
        ]
    });
};
export default AcceptInvitationPage;
